/**
 * Safe post-login destination for the consumer booking site.
 */

const SAME_ORIGIN_BASE = "https://invalid.local";

export type SignInRedirect =
  | { kind: "path"; path: string }
  | { kind: "back" };

/**
 * Decode percent-encoding until stable so `%2e%2e` / `%252e` collapse to `..`.
 */
function decodePathname(pathname: string): string | null {
  let decoded = pathname;
  try {
    for (let i = 0; i < 8; i += 1) {
      const once = decodeURIComponent(decoded);
      if (once === decoded) {
        return decoded;
      }
      decoded = once;
    }
  } catch {
    return null;
  }
  return null;
}

/**
 * Resolve `.` / `..` and encoded-dot variants to a same-origin path + query.
 */
function canonicalizeNextPath(next: string): string | null {
  if (next.includes("\\") || next.includes("\0")) {
    return null;
  }

  let url: URL;
  try {
    url = new URL(next, SAME_ORIGIN_BASE);
  } catch {
    return null;
  }
  if (url.origin !== SAME_ORIGIN_BASE) {
    return null;
  }

  const decodedPathname = decodePathname(url.pathname);
  if (
    decodedPathname == null ||
    decodedPathname.includes("\\") ||
    decodedPathname.includes("://")
  ) {
    return null;
  }

  let resolved: URL;
  try {
    resolved = new URL(decodedPathname, SAME_ORIGIN_BASE);
  } catch {
    return null;
  }
  if (resolved.origin !== SAME_ORIGIN_BASE) {
    return null;
  }

  return `${resolved.pathname}${url.search}${url.hash}`;
}

/**
 * Return a same-stylist relative path from `next`, or null if unsafe.
 */
export function getSafeNextPath(
  search: string,
  stylistCode: string
): string | null {
  if (!stylistCode) {
    return null;
  }

  const params = new URLSearchParams(search.replace(/^\?/, ""));
  const next = params.get("next");
  if (!next) {
    return null;
  }

  const canonical = canonicalizeNextPath(next);
  if (!canonical) {
    return null;
  }

  const firstSegment = canonical.split("/").filter(Boolean)[0];
  if (firstSegment !== stylistCode) {
    return null;
  }

  return canonical;
}

/**
 * Where to send the consumer after a successful sign-in.
 */
export function getSignInRedirect(
  search: string,
  stylistCode: string
): SignInRedirect {
  const next = getSafeNextPath(search, stylistCode);
  if (next) {
    return { kind: "path", path: next };
  }

  const params = new URLSearchParams(search.replace(/^\?/, ""));
  if (params.has("notlogged")) {
    return { kind: "back" };
  }

  return { kind: "path", path: `/${stylistCode}/bookings` };
}
